// frontend/src/useCases/useMasterCodeUsage.ts
import { useEffect, useRef, useState } from "react";
import type { TMasterCodeUsage } from "@/ui/molecules";

export interface TMasterCodeUsageState {
    usage: TMasterCodeUsage | null;
    isLoading: boolean;
    hasError: boolean;
}

const IDLE: TMasterCodeUsageState = { usage: null, isLoading: false, hasError: false };

/**
 * Load which mappings still use a master CoA account or property code while its
 * delete confirmation is open. `id` is null while no confirmation is open; a
 * response for a code the user has since moved away from is dropped.
 */
export const useMasterCodeUsage = (
    id: string | null,
    loadUsage: (id: string) => Promise<TMasterCodeUsage>,
): TMasterCodeUsageState => {
    const [state, setState] = useState<TMasterCodeUsageState>(IDLE);
    // Latest loader without re-running the effect when the caller passes a fresh closure.
    const loadRef = useRef(loadUsage);
    loadRef.current = loadUsage;

    useEffect(() => {
        if (id === null) {
            setState(IDLE);
            return;
        }
        let current = true;
        setState({ usage: null, isLoading: true, hasError: false });
        loadRef.current(id).then(
            (usage) => {
                if (current) setState({ usage, isLoading: false, hasError: false });
            },
            () => {
                if (current) setState({ usage: null, isLoading: false, hasError: true });
            },
        );
        return () => {
            current = false;
        };
    }, [id]);

    return state;
};
